import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, Pencil, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sprint 1.0c F18 fix — split a post's content into:
//   • `text`       — the free-text portion the user can edit
//   • `shortcodes` — the [product:id:label] / [community:id:label] tokens
//     that must be preserved as-is. We currently support `product` and
//     `community` (the ones generated by the composer). Anything we don't
//     recognise is left in the editable text so we never accidentally hide
//     content from the user.
const SHORTCODE_RE = /\[(product|community):([^:\]]+):([^\]]+)\]/g;

const splitShortcodes = (raw) => {
  const text = raw || '';
  const tokens = [];
  let m;
  const re = new RegExp(SHORTCODE_RE.source, 'g');
  while ((m = re.exec(text)) !== null) {
    tokens.push({ raw: m[0], kind: m[1], id: m[2], label: m[3] });
  }
  const stripped = text.replace(SHORTCODE_RE, '').replace(/\n{3,}/g, '\n\n').trim();
  return { editableText: stripped, tokens };
};

/**
 * Sprint 1.0c F18 — Edit a post's text content.
 *
 * Used by both Feed.js and ProfileWall.js. Owner-only (the caller decides when
 * to render the trigger). Attachments (event_share, poll, products, media)
 * are NOT editable — only the free-text content. Polls with existing votes
 * are blocked server-side (409).
 *
 * Contract:
 *   <EditPostModal post={post} isOpen onClose onSaved={(updatedPost) => …} />
 */
const EditPostModal = ({ post, isOpen, onClose, onSaved }) => {
  // Sprint 1.0c F18 fix — extract shortcodes ONCE on mount so the textarea
  // shows only the editable text and the attached references are immutable.
  const initial = splitShortcodes(post?.content);
  const [content, setContent] = useState(() => initial.editableText);
  const [shortcodeTokens] = useState(() => initial.tokens);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Defer to ensure the modal is painted before we focus.
      const t = setTimeout(() => {
        textareaRef.current?.focus();
        const len = textareaRef.current?.value?.length || 0;
        textareaRef.current?.setSelectionRange(len, len);
      }, 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen || !post) return null;

  const hasChanged = (content || '').trim() !== (initial.editableText || '').trim();
  const isPollWithVotes = post.post_type === 'poll'
    && (post.poll?.options || []).some((o) => (o?.votes || 0) > 0);

  const handleSave = async () => {
    if (!hasChanged || saving) return;
    setSaving(true);
    try {
      // Sprint 1.0c F18 fix — reinject shortcodes intact at the end of the
      // saved content. This preserves the link to the attached product /
      // community even though the user can no longer see the raw token.
      const trimmedText = (content || '').trim();
      const tokensSuffix = shortcodeTokens.length
        ? '\n\n' + shortcodeTokens.map((t) => t.raw).join('\n')
        : '';
      const finalContent = (trimmedText + tokensSuffix).trim();
      const { data } = await axios.patch(
        `${API}/posts/${post.post_id}`,
        { content: finalContent },
        { withCredentials: true },
      );
      toast.success('Publication modifiée');
      if (onSaved) onSaved(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="edit-post-modal-overlay"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-post-modal"
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
              <Pencil className="h-3.5 w-3.5 text-purple-600 dark:text-purple-300" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Modifier la publication</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            data-testid="edit-post-close"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {isPollWithVotes && (
            <div className="text-xs text-amber-700 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2" data-testid="edit-post-poll-warning">
              Ce sondage a déjà reçu des votes, son texte ne peut plus être modifié pour préserver l&apos;équité.
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isPollWithVotes || saving}
            placeholder="Que voulez-vous partager ?"
            className="w-full min-h-[140px] max-h-[40vh] resize-y rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-60"
            data-testid="edit-post-textarea"
            maxLength={5000}
          />

          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
            <span>{content.length}/5000</span>
            {post.post_type === 'event_share' && (
              <span className="italic">L&apos;événement attaché ne sera pas modifié.</span>
            )}
          </div>

          {/* Sprint 1.0c F18 fix — read-only shortcode chips. The user can SEE
              what's attached (product / community) without being able to break
              the marker. The token is reinjected on save. */}
          {shortcodeTokens.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700" data-testid="edit-post-shortcodes">
              <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                Le contenu attaché ne sera pas modifié&nbsp;:
              </p>
              <ul className="space-y-1.5">
                {shortcodeTokens.map((t, idx) => (
                  <li
                    key={`${t.kind}-${t.id}-${idx}`}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 text-xs"
                    data-testid={`edit-post-shortcode-${t.kind}-${idx}`}
                  >
                    <Link2 className="h-3 w-3 text-gray-400" />
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 shrink-0">
                      {t.kind === 'product' ? 'Produit' : t.kind === 'community' ? 'Communauté' : t.kind}
                    </span>
                    <span className="text-gray-700 dark:text-gray-200 truncate" title={t.label}>{t.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            data-testid="edit-post-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanged || isPollWithVotes || saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#7F77DD' }}
            data-testid="edit-post-save"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPostModal;
